import * as React from 'react';
import { Bell, ChevronDown, Ellipsis, LifeBuoy, Search, X } from 'lucide-react';
import { PageHeadProvider, useIsMobileLayout, type PageHeadSlots } from './page-head.js';
import { ParcelBackdrop } from './parcel-backdrop.js';
import { readCollapsedNavSections, writeCollapsedNavSections } from '../stores/session.js';

/**
 * Krok UXR3 — shell adminu podle SCHVALENE predlohy UX v3
 * `docs/design/ux-v3/` (`Main.dc.html` desktop, `Mobil.dc.html` 390 px;
 * R.B. 2. 10. 2026 23:49). Sazba je v `src/styles/admin-v2.css`.
 *
 * Co se proti UXR1/UXR2 zmenilo a PROC:
 *
 * 1. **Chrome aplikace zije v TMAVEM PANELU vlevo.** Predloha na desktopu
 *    zadnou horni listu nema — obsah zacina hned nahore (u detailu zakazky je
 *    to bily pas hlavicky). Znacka s nazvem kancelare, hledani, menu i
 *    paticka (zvonek, avatar, „Nahlásit problém") jsou proto v panelu.
 * 2. **Horni lista je uz jen MOBILNI** (predloha `Mobil.dc.html`: tmavy pas
 *    s nazvem obrazovky). Nese „‹", nazev a „⋯" — nic z toho na desktopu
 *    neni potreba (zpet vede drobecek, „⋯" stoji v hlavicce stranky).
 * 3. **Mobilni „Více" otevira TENTYZ panel**, ne druhy seznam. Do UXR3 byl
 *    vedle panelu jeste `rh-a2-msheet` se sestavenou podmnozinou polozek a
 *    po presunu chrome do paticky panelu by se na telefon nedostal zvonek,
 *    jazyk, Nastaveni ani Odhlaseni. Jeden DOM to resi bez druhych
 *    `data-testid` — panel je na mobilu skryty, dokud ho „Více" neotevre.
 * 4. **Krok FIXE1: AVATAR S NABIDKOU PROFILU JE NA MOBILU V HORNI LISTE.**
 *    Od UXV4a jsou v nabidce profilu nastroje (vcetne „Úkoly") a od UXV4p
 *    i „Velikost písma" a „Vzhled" — na mobilu k nim ale vedla cesta jen
 *    pres bod 3, tedy DVE klepnuti („Více" → avatar v paticce panelu), a
 *    scenar S13 na 390 px padal na `element is not visible` (e2e preview
 *    79b3363c, 4. 10. 14:21). Zadani FIXE1 bod 1 chce JEDNO klepnuti
 *    z kazde obrazovky, proto se `userSlot` na mobilnim rozvrzeni vykresli
 *    v horni liste.
 *
 *    JEDNA komponenta a JEDEN uzel v DOM: misto se vybira
 *    `useIsMobileLayout()` (tentyz breakpoint 900 px jako sazba a tentyz
 *    SSOT, kterym uz `PageHead` rozhoduje o primarni akci). Dve kopie
 *    (jedna schovana CSS) by znamenaly dve stejna `data-testid`
 *    (`avatar-menu-trigger` — strict mode Playwrightu) a dvojity tab-stop.
 *
 * ROZHODNUTI UXR1-1 plati dal: shell zije ve web-adminu, kit `AppShell`
 * (@redhand/ui) zustava BEZE ZMENY. Tentyz kit shell pouzivaji jeste portal,
 * kokpit, web-public a galerie.
 *
 * Co shell NEdela: nerozhoduje o obsahu menu (dodava ho `lib/nav.ts` pres
 * `layout.tsx`) ani o hlavicce stranky (tu kresli obrazovka pres `PageHead`).
 */

export interface AdminShellNavItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly active: boolean;
  /**
   * Krok UXF4 bod 4: pristupny nazev, kdyz se cely popisek do dlazdice
   * nevejde („Nová" na spodni liste = „Nová zakázka" pro ctecku).
   */
  readonly ariaLabel?: string;
  /** Pocet VECI NA UZIVATELI u polozky (predloha: oranzovy odznak). */
  readonly count?: number;
  readonly icon: React.ReactNode;
}

/**
 * Krok UXN1 — skupina leveho menu. `label` chybi u pinned polozek NAD
 * skupinami (Dnešek, Nová zakázka) — ty se nikdy nesbaluji a nemaji
 * toggle. Skupina S nadpisem je sbalitelna, stav si pamatuje shell per
 * prohlizec (`stores/session.ts`).
 */
export interface AdminShellNavGroup {
  readonly id: string;
  readonly label?: string | undefined;
  readonly items: readonly AdminShellNavItem[];
}

export interface AdminShellTexts {
  readonly skipLinkLabel: string;
  readonly navLabel: string;
  /** Popisek tlacitka, ktere na mobilu otevira cely panel („Více"). */
  readonly moreMobileLabel: string;
  readonly closeLabel: string;
  readonly reportProblemLabel: string;
}

export interface AdminShellProps {
  /**
   * Krok SML2 bod 1 — REZIM SOUSTREDENI: `true` = bez hlavniho menu
   * (tmaveho panelu) a bez spodni listy mobilu. Obrazovka si tim bere celou
   * sirku a clovek z ni neodbiha; o tom, KTERE cesty to jsou, rozhoduje
   * `isFocusModeRoute` v `lib/nav.ts`, ne shell.
   */
  readonly focusMode?: boolean | undefined;
  /**
   * Krok BRAND2 — ZNACKA PRODUKTU JE LOGO, NE TEXT (zadani bod 1).
   * Shell dostava hotovy uzel (`<ProductLogo>`), protoze o tom, KTEROU
   * variantu loga vykreslit, rozhoduje ucinny rezim vzhledu — a ten zna
   * komponenta znacky, ne shell. Do BRAND2 tu stal `productName: string`
   * a shell z nej sazel text.
   */
  readonly productLogo: React.ReactNode;
  /** Nazev kancelare (predloha: „MV reality" velke, pod tim znacka produktu). */
  readonly orgName: string | null;
  readonly homeHref: string;
  /**
   * Krok UXN1 — leve menu = VSECHNY pracovni moduly, seskupene (Obchod ·
   * Práce · Peníze · Přehledy · Kancelář). Prvni skupina (`label`
   * `undefined`) nese pinned „Dnešek" a „Nová zakázka" a nikdy se nesbaluje.
   */
  readonly mainNav: readonly AdminShellNavGroup[];
  /**
   * Spodni lista mobilu — CTYRI polozky (Dnešek · Zakázky · Nová · Kalendář)
   * z tehoz menu jako panel, aby lista a panel nerikaly dve ruzne veci.
   * PATOU dlazdici „Více" dopisuje shell sam (krok UXF4 bod 1) — otevira
   * jeho vlastni panel, takze stav listy nema kam vracet.
   */
  readonly mobileNav: readonly AdminShellNavItem[];
  /** Zmena hodnoty zavre mobilni nabidku „Více" (shell nezna router). */
  readonly routeKey: string;
  /**
   * Vykresleni polozky routerem (`<Link>`); shell dodava tridu, obsah a MISTO,
   * kde polozka stoji. `place` je potreba kvuli `data-testid`: tataz polozka
   * je v panelu i ve spodni liste, a dve stejna testid na strance by
   * znamenala, ze se na ni test (ani ctecka) nedokaze odkazat jednoznacne.
   */
  readonly renderNavItem: (
    item: AdminShellNavItem,
    className: string,
    content: React.ReactNode,
    place: 'side' | 'mobile',
  ) => React.ReactNode;
  readonly search: {
    readonly label: string;
    readonly placeholder: string;
    readonly shortcut: string;
    readonly onSearch: (query: string) => void;
  };
  readonly notifications: {
    readonly label: string;
    readonly count?: number;
    readonly onOpen: () => void;
  };
  /**
   * Avatar s menu (UXA1) — sklada `layout.tsx`.
   *
   * Krok FIXE1: shell ho vykresluje JEDNOU, ale NA JINEM MISTE podle
   * rozvrzeni — na desktopu v paticce panelu (predloha v4 § Desktop), na
   * mobilu v HORNI LISTE. Duvod je v hlavicce souboru, bod 4.
   */
  readonly userSlot: React.ReactNode;
  /**
   * Krok MOB1 bod 3: `undefined` = hlaseni problemu NENI na serveru zapojene
   * (`flags.supportReporting`), takze shell tlacitko vubec nevykresli. Zadani:
   * „Bez env je tlacitko skryte" — zadna falesna funkce.
   */
  readonly onReportProblem?: (() => void) | undefined;
  readonly texts: AdminShellTexts;
  readonly children: React.ReactNode;
}

function navContent(item: AdminShellNavItem): React.ReactNode {
  return (
    <>
      <span className="rh-a2-ico" aria-hidden="true">
        {item.icon}
      </span>
      <span className="rh-a2-side__label">{item.label}</span>
      {item.count !== undefined && item.count > 0 ? (
        <span className="rh-a2-side__count">{item.count}</span>
      ) : null}
    </>
  );
}

function mobileNavContent(item: AdminShellNavItem): React.ReactNode {
  return (
    <>
      <span className="rh-a2-ico" aria-hidden="true">
        {item.icon}
      </span>
      {item.label}
      {item.count !== undefined && item.count > 0 ? (
        <span className="rh-a2-side__count">{item.count}</span>
      ) : null}
    </>
  );
}

export function AdminShell({
  focusMode = false,
  productLogo,
  orgName,
  homeHref,
  mainNav,
  mobileNav,
  routeKey,
  renderNavItem,
  search,
  notifications,
  userSlot,
  onReportProblem,
  texts,
  children,
}: AdminShellProps): React.JSX.Element {
  const [headSlot, setHeadSlot] = React.useState<HTMLElement | null>(null);
  const [menuSlot, setMenuSlot] = React.useState<HTMLElement | null>(null);
  const [backSlot, setBackSlot] = React.useState<HTMLElement | null>(null);
  const [pageTitle, setPageTitle] = React.useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  /** Krok FIXE1 — kam patri `userSlot` (hlavicka souboru, bod 4). */
  const isMobile = useIsMobileLayout();

  /**
   * Krok UXN1 — sbalene skupiny leveho menu, per prohlizec. Stejne uloziste
   * jako puvodni skupiny „Nástroje"/„Další nástroje" z kroku UXA1
   * (`readCollapsedNavSections`/`writeCollapsedNavSections` v
   * `stores/session.ts`) — UXV4a je prestalo volat, kdyz menu zmenila na
   * „podle role", ale klic i funkce zustaly (SSOT, ne druha kopie).
   */
  const [collapsedGroups, setCollapsedGroups] = React.useState<ReadonlySet<string>>(() =>
    readCollapsedNavSections(),
  );

  const toggleGroup = (groupId: string): void => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      writeCollapsedNavSections(next);
      return next;
    });
  };

  // Prechod na jinou obrazovku nabidku zavre — jinak zustane prekryvat obsah.
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [routeKey]);

  /*
   * Krok UXP4c (P3, nalez „na strankach pod Vice neni ve spodni liste nic
   * zvyrazneno"): „Vice" je ctvrta dlazdice, ktera nema svou vlastni cestu —
   * je aktivni, kdyz je aktivni NEKTERA polozka hlavniho menu, ale ZADNA
   * z pevnych ctyr dlazdic (Dnesek/Zakazky/Nova/Kalendar) neni — tedy
   * uzivatel stoji na strance (Fakturace, Smlouvy, Portaly, …), ke ktere
   * vede jen panel „Vice".
   */
  const moreActive =
    mainNav.some((group) => group.items.some((item) => item.active)) &&
    !mobileNav.some((item) => item.active);

  const slots = React.useMemo<PageHeadSlots>(
    () => ({ headSlot, menuSlot, backSlot, setTitle: setPageTitle }),
    [headSlot, menuSlot, backSlot],
  );

  const sideItems = (items: readonly AdminShellNavItem[]): React.ReactNode =>
    items.map((item) => (
      <li key={item.id}>
        {renderNavItem(
          item,
          `rh-a2-side__item${item.active ? ' rh-a2-side__item--active' : ''}`,
          navContent(item),
          'side',
        )}
      </li>
    ));

  return (
    <PageHeadProvider value={slots}>
      <div
        className="rh-root rh-a2-shell"
        data-menu-open={String(mobileMenuOpen)}
        data-focus-mode={String(focusMode)}
      >
        {/* Krok GRAF1: plocha znacky pod sklem — jedno misto pro celou aplikaci. */}
        <ParcelBackdrop />

        <a href="#main-content" className="rh-skip-link">
          {texts.skipLinkLabel}
        </a>

        {/* Horni lista je POUZE mobilni (predloha `Mobil.dc.html`) — na
            desktopu ji CSS nevykresluje, chrome zije v tmavem panelu. */}
        <header className="rh-a2-topbar">
          {/* Mobil: zpetna strelka „‹" pred nazvem obrazovky (FIX6 bod 5). */}
          <span className="rh-a2-mtop__back" ref={setBackSlot} />

          <span className="rh-a2-mtop__title" data-testid="mobile-page-title" hidden={pageTitle === null}>
            {pageTitle}
          </span>

          <div className="rh-a2-topbar__right">
            {/* Mobil: nabidka „⋯" aktualni obrazovky — plni ji `PageHead`. */}
            <span className="rh-a2-mtop__menu" ref={setMenuSlot} />
            {/*
              Krok UXF4 bod 1 (nalez N1): tlacitko „Více" uz tady NENI — stoji
              jako pata dlazdice spodni listy S POPISKEM. Ikona `…` v horni
              liste nebyla oznacena cesta k Fakturaci, Smlouvam, Portalum ani
              Reportum; v liste je popisek videt a cil je na dosah palce.
            */}
            {/*
              Krok FIXE1 — nabidka profilu na JEDNO klepnuti i na mobilu
              (hlavicka souboru, bod 4). Stoji az za „Více", tedy u prave
              hrany, jak je profil v aplikacich zvykem.
            */}
            {isMobile ? <div className="rh-a2-mtop__user">{userSlot}</div> : null}
          </div>
        </header>

        <div className="rh-a2-body">
          {/*
            Tmavy panel. Na mobilu je to zaroven nabidka „Více" (viz hlavicka
            souboru, bod 3) — proto `data-testid="mobile-nav-sheet"`: je to
            TENTYZ prvek, ktery „Více" otevira, a sada e2e nad nim meri, ze
            se zavrenou nabidkou videt neni.
          */}
          <nav className="rh-a2-side" aria-label={texts.navLabel} data-testid="mobile-nav-sheet">
            {/*
              Krok BRAND2 bod 1 — v zahlavi stoji LOGO, ne nazev produktu
              pismem. Rozvrzeni se nemeni (manual str. 7: „definuje vzhled,
              nikoli nové rozvržení"): nahore iniciala a nazev KANCELARE,
              pod nim znacka produktu — jen misto slova „Makléřov" je tam
              horizontalni logo v minimalni sirce 140 px. Bez kancelare
              (prvni prihlaseni) nese znacku cely blok sama.
            */}
            <a href={homeHref} className="rh-a2-brand">
              {orgName === null ? (
                productLogo
              ) : (
                <>
                  <span className="rh-a2-brand__mark" aria-hidden="true">
                    {orgName.trim().charAt(0).toUpperCase()}
                  </span>
                  <span className="rh-a2-brand__names">
                    <span className="rh-a2-brand__name">{orgName}</span>
                    <span className="rh-a2-brand__org">{productLogo}</span>
                  </span>
                </>
              )}
            </a>

            <form
              role="search"
              className="rh-a2-search"
              onSubmit={(event) => {
                event.preventDefault();
                const input = event.currentTarget.elements.namedItem('q');
                if (input instanceof HTMLInputElement) search.onSearch(input.value);
              }}
            >
              <span className="rh-a2-ico rh-a2-ico--sm rh-a2-search__icon" aria-hidden="true">
                <Search size={16} strokeWidth={2} />
              </span>
              <input type="search" name="q" aria-label={search.label} placeholder={search.placeholder} />
              {/*
                Krok UXP4c (P3, nalez „mobilni Vice ukazuje Ctrl K"): klavesova
                zkratka nema na telefonu smysl (nema fyzicky Ctrl) — panel
                „Více" vykresluje TENTYZ `<nav>` jako levé menu desktopu.
              */}
              {isMobile ? null : <kbd>{search.shortcut}</kbd>}
            </form>

            <ul className="rh-a2-side__list">
              {mainNav.map((group) =>
                group.label === undefined ? (
                  <React.Fragment key={group.id}>{sideItems(group.items)}</React.Fragment>
                ) : (
                  <li key={group.id} className="rh-a2-side__group">
                    <button
                      type="button"
                      className="rh-a2-side__group-toggle"
                      aria-expanded={!collapsedGroups.has(group.id)}
                      onClick={() => toggleGroup(group.id)}
                      data-testid={`nav-section-toggle-${group.id}`}
                    >
                      <span className="rh-a2-side__group-label">{group.label}</span>
                      <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
                        <ChevronDown size={14} strokeWidth={2} />
                      </span>
                    </button>
                    {collapsedGroups.has(group.id) ? null : (
                      <ul className="rh-a2-side__list rh-a2-side__group-list">{sideItems(group.items)}</ul>
                    )}
                  </li>
                ),
              )}
            </ul>

            <div className="rh-a2-side__foot">
              {onReportProblem !== undefined ? (
                <button
                  type="button"
                  className="rh-a2-side__item"
                  onClick={onReportProblem}
                  data-testid="footer-report-problem"
                >
                  <span className="rh-a2-ico" aria-hidden="true">
                    <LifeBuoy size={18} strokeWidth={1.75} />
                  </span>
                  <span className="rh-a2-side__label">{texts.reportProblemLabel}</span>
                </button>
              ) : null}
              <button
                type="button"
                className="rh-a2-bell"
                aria-label={
                  notifications.count !== undefined && notifications.count > 0
                    ? `${notifications.label} (${String(notifications.count)})`
                    : notifications.label
                }
                onClick={notifications.onOpen}
                data-testid="shell-bell"
              >
                <span className="rh-a2-ico" aria-hidden="true">
                  <Bell size={18} strokeWidth={1.75} />
                </span>
                {notifications.count !== undefined && notifications.count > 0 ? (
                  <span className="rh-a2-bell__count" aria-hidden="true">
                    {notifications.count}
                  </span>
                ) : null}
              </button>
              {/* Na mobilu uz avatar stoji v horni liste (bod 4) — tady by byl podruhe. */}
              {isMobile ? null : userSlot}
            </div>
          </nav>

          <div className="rh-a2-col">
            <main id="main-content" className="rh-a2-main">
              <div className="rh-a2-slot" ref={setHeadSlot} />
              {/*
                Krok VZH1 (doplneni R.B. 6. 10. bod 2) — JEDNA KOSTRA STRANKY.
                Obsah kazde obrazovky stoji v temze kontejneru jako hlavicka
                nad nim (`.rh-a2-pagehead > *`): tataz maximalni sirka, tytez
                okraje. Drive mel kontejner jen tucet prevedenych obrazovek
                (`.rh-a2-page`) a hlavicka zadny — na sirokem monitoru stal
                nadpis u menu a obsah uprostred.
              */}
              <div className="rh-a2-content">{children}</div>
            </main>
          </div>
        </div>

        {/*
          Spodni lista predlohy `Main.dc.html` — pet dlazdic, aktivni akcentem.
          Krok UXF4 bod 2 (nalez N10): je to JEDINY pevny prvek u spodni hrany.
          Druha lepena lista `.rh-a2-mbar` s primarni akci stranky nad ni uz
          neexistuje — akce zustava v hlavicce stranky (`page-head.tsx`),
          protoze dve listy nad sebou ukrajovaly z 844 px vysky telefonu.
        */}
        <nav className="rh-a2-mnav" aria-label={texts.navLabel}>
          {mobileNav.map((item) => (
            <React.Fragment key={item.id}>
              {renderNavItem(item, item.active ? 'is-on' : '', mobileNavContent(item), 'mobile')}
            </React.Fragment>
          ))}
          {/*
            Krok UXF4 bod 1 — „Více" otevira TENTYZ panel jako leve menu
            (`mobile-nav-sheet`), takze skupiny Obchod · Práce · Peníze ·
            Přehledy · Kancelář jsou na telefonu tytez jako na desktopu.
            Testid zustava `mobile-nav-more` z UXV4a — je to tatáz akce, jen
            na jinem miste, a sady e2e na nej uz miri.
          */}
          <button
            type="button"
            className={moreActive ? 'is-on' : undefined}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((open) => !open)}
            data-testid="mobile-nav-more"
          >
            <span className="rh-a2-ico" aria-hidden="true">
              {mobileMenuOpen ? <X size={22} strokeWidth={1.9} /> : <Ellipsis size={22} strokeWidth={1.9} />}
            </span>
            {texts.moreMobileLabel}
          </button>
        </nav>
      </div>
    </PageHeadProvider>
  );
}
